import BackgroundShowCommand from '../../domain/entities/commands/BackgroundShowCommand';
import CharacterHideCommand from '../../domain/entities/commands/CharacterHideCommand';
import CharacterShowCommand from '../../domain/entities/commands/CharacterShowCommand';
import CharacterUpdateCommand from '../../domain/entities/commands/CharacterUpdateCommand';
import ClearCommand from '../../domain/entities/commands/ClearCommand';
import Command from '../../domain/entities/commands/Command';
import CompositeParallelCommand from '../../domain/entities/commands/CompositeParallelCommand';
import SpeechTextShowCommand from '../../domain/entities/commands/SpeechTextShowCommand';
import DescriptiveTextShowCommand from '../../domain/entities/commands/DescriptiveTextShowCommand';
import IllustrationShowCommand from '../../domain/entities/commands/IllustrationShowCommand';
import FullScreenIllustrationShowCommand from '../../domain/entities/commands/FullScreenIllustrationShowCommand';
import BackgroundMusicShowCommand from '../../domain/entities/commands/BackgroundMusicShowCommand';
import BackgroundMusicHideCommand from '../../domain/entities/commands/BackgroundMusicHideCommand';

import Position from '../../domain/value_objects/Position';

export type BackgroundCommand = BackgroundShowCommand;

export type CharacterCommand =
  | CharacterShowCommand
  | CharacterUpdateCommand
  | CharacterHideCommand;

export default class Frame {
  public static copy(frame: Frame): Frame {
    const newFrame = new Frame(frame.enabledZoom);
    newFrame.sceneId = frame.sceneId;

    newFrame.background = frame.background;

    newFrame.leftCharacter = frame.leftCharacter;
    newFrame.centerCharacter = frame.centerCharacter;
    newFrame.rightCharacter = frame.rightCharacter;

    newFrame.zoomCharacter = frame.zoomCharacter;
    newFrame.zoomFromWaiting = frame.zoomFromWaiting;
    newFrame.pendingWaitingZoom = new Set(frame.pendingWaitingZoom);
    newFrame.illustration = frame.illustration;
    newFrame.fullScreenIllustration = frame.fullScreenIllustration;
    newFrame.backgroundMusic = frame.backgroundMusic;

    return newFrame;
  }

  public sceneId: number | null = null;

  public background: BackgroundCommand | null = null;

  public leftCharacter: CharacterCommand | null = null;
  public centerCharacter: CharacterCommand | null = null;
  public rightCharacter: CharacterCommand | null = null;

  public zoomCharacter: CharacterCommand | null = null;
  // 現在の zoom が「待機表示されたキャラの初発話」由来か（pre-zoom 判定用）
  public zoomFromWaiting = false;
  // 待機表示されたが、まだ喋っていないポジション
  public pendingWaitingZoom: Set<Position> = new Set();

  public illustration: IllustrationShowCommand | null = null;
  public fullScreenIllustration: FullScreenIllustrationShowCommand | null =
    null;
  public backgroundMusic:
    | BackgroundMusicShowCommand
    | BackgroundMusicHideCommand
    | null = null;

  constructor(private enabledZoom: boolean = false) {}

  public update(command: Command) {
    this.sceneId = command.sceneId;
    this.zoomCharacter = null;
    this.zoomFromWaiting = false;
    if (!(command instanceof DescriptiveTextShowCommand)) {
      this.illustration = null;
    }
    if (this.backgroundMusic instanceof BackgroundMusicHideCommand) {
      this.backgroundMusic = null;
    }
    if (!(command instanceof ClearCommand)) {
      this.fullScreenIllustration = null;
    }
    if (command instanceof BackgroundShowCommand) {
      this.background = command;
    } else if (
      command instanceof CharacterShowCommand ||
      command instanceof CharacterUpdateCommand ||
      command instanceof CharacterHideCommand
    ) {
      switch (command.position) {
        case Position.Left:
          this.leftCharacter = command;
          break;
        case Position.Center:
          this.centerCharacter = command;
          break;
        case Position.Right:
          this.rightCharacter = command;
          break;
        default:
          throw new Error('Does not match Position');
      }
      if (command instanceof CharacterShowCommand && command.options.waiting) {
        this.pendingWaitingZoom.add(command.position);
      } else {
        this.pendingWaitingZoom.delete(command.position);
      }
    } else if (command instanceof IllustrationShowCommand) {
      this.illustration = command;
    } else if (command instanceof FullScreenIllustrationShowCommand) {
      this.fullScreenIllustration = command;
    } else if (command instanceof CompositeParallelCommand) {
      command.commands.forEach(subCommand => {
        this.update(subCommand);
      });
    } else if (command instanceof BackgroundMusicShowCommand) {
      this.backgroundMusic = command;
    } else if (command instanceof BackgroundMusicHideCommand) {
      this.backgroundMusic = command;
    } else if (command instanceof SpeechTextShowCommand) {
      if (command.speechBalloon.getPositions().length === 1) {
        const position = command.speechBalloon.getPositions()[0];
        if (this.enabledZoom && this.visibleMarkedCharacter(position)) {
          this.zoomCharacter = this.characterAt(position);
          this.zoomFromWaiting = this.pendingWaitingZoom.has(position);
        }
        this.pendingWaitingZoom.delete(position);
      }
    } else if (command instanceof ClearCommand) {
      this.background = null;
      this.leftCharacter = null;
      this.centerCharacter = null;
      this.rightCharacter = null;
      this.pendingWaitingZoom.clear();
      if (
        !(
          this.backgroundMusic &&
          this.backgroundMusic instanceof BackgroundMusicShowCommand &&
          this.backgroundMusic.options.fallThrough
        )
      ) {
        this.backgroundMusic = null;
      }
    }
  }

  public characterAt(position: Position): CharacterCommand | null {
    switch (position) {
      case Position.Left:
        return this.leftCharacter;
      case Position.Center:
        return this.centerCharacter;
      case Position.Right:
        return this.rightCharacter;
      default:
        throw new Error('Does not match Position');
    }
  }

  public visibleCharacter(position: Position): boolean {
    switch (position) {
      case Position.Left:
        return !!(
          this.leftCharacter &&
          !(this.leftCharacter instanceof CharacterHideCommand)
        );
      case Position.Center:
        return !!(
          this.centerCharacter &&
          !(this.centerCharacter instanceof CharacterHideCommand)
        );
      case Position.Right:
        return !!(
          this.rightCharacter &&
          !(this.rightCharacter instanceof CharacterHideCommand)
        );
      default:
        throw new Error('Does not match Position');
    }
  }

  public visibleCharacterCount(): number {
    let count = 0;
    if (this.visibleCharacter(Position.Left)) {
      count += 1;
    }
    if (this.visibleCharacter(Position.Center)) {
      count += 1;
    }
    if (this.visibleCharacter(Position.Right)) {
      count += 1;
    }
    return count;
  }

  public visibleMarkedCharacter(position: Position): boolean {
    switch (position) {
      case Position.Left:
        return !!(
          this.visibleCharacter(Position.Left) && this.leftCharacter?.mark
        );
      case Position.Center:
        return !!(
          this.visibleCharacter(Position.Center) && this.centerCharacter?.mark
        );
      case Position.Right:
        return !!(
          this.visibleCharacter(Position.Right) && this.rightCharacter?.mark
        );
      default:
        throw new Error('Does not match Position');
    }
  }

  public copy(): Frame {
    return Frame.copy(this);
  }
}
